<template>
  <div class="settings-page" :key="refreshKey">
    <header>
      <h1 class="text-2xl font-bold mb-4">{{ languageStore.t('home.settings.title') }}</h1>
      <p class="text-gray-600">{{ languageStore.t('home.settings.description') }}</p>
    </header>

    <div v-if="isLoading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
    </div>

    <div v-else class="mt-8">
      <!-- Kullanıcı Bilgileri -->
      <div class="bg-white shadow rounded-lg p-6 mb-6">
        <h2 class="text-xl font-semibold mb-4">{{ languageStore.t('home.settings.userInfo') }}</h2>
        <div class="space-y-2">
          <p><span class="font-medium">{{ languageStore.t('home.settings.email') }}:</span> {{ user?.email }}</p>
        </div>
      </div>

      <!-- Bildirim Ayarları -->
      <div class="bg-white shadow rounded-lg p-6">
        <h2 class="text-xl font-semibold mb-4">{{ languageStore.t('home.settings.notifications') }}</h2>
        
        <div class="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
          <div class="flex items-center">
            <div class="flex items-center justify-center w-8 h-8 rounded-full bg-teal-100 text-teal-500 mr-3">
              <i class="fas fa-envelope"></i>
            </div>
            <div>
              <p class="font-medium">{{ languageStore.t('home.settings.emailNotifications') }}</p>
              <p class="text-sm text-gray-500">{{ languageStore.t('home.settings.emailNotificationsDesc') }}</p>
            </div>
          </div>
          <label class="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              v-model="emailNotifications" 
              @change="updateEmailNotifications"
              class="sr-only peer"
              :disabled="isSaving"
            >
            <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { supabase } from '@/config/supabase'
import { useAuthStore } from '@/stores/auth'
import { useLanguageStore } from '@/stores/languageStore'

const toast = useToast()
const authStore = useAuthStore()
const languageStore = useLanguageStore()
const user = ref(authStore.user)

const emailNotifications = ref(true)
const isLoading = ref(true)
const isSaving = ref(false)
const currentLanguage = ref<'tr' | 'en'>(languageStore.currentLocale)
const refreshKey = computed(() => `settings-${currentLanguage.value}-${Date.now()}`)

// Dil değişikliklerini dinle
let cleanupLanguageListener: (() => void) | null = null

onMounted(async () => {
  if (!user.value?.id) {
    toast.error(languageStore.t('common.errors.notAuthenticated'))
    isLoading.value = false
    return
  }
  
  // Dil değişikliği dinleyicisini kaydet
  cleanupLanguageListener = languageStore.onLanguageChange((lang) => {
    console.log('📢 Settings sayfasında dil değişikliği algılandı:', lang)
    // Dil değişikliğinde currentLanguage değerini güncelle
    // Bu, refreshKey computed property'nin yeniden hesaplanmasını ve
    // bileşenin tamamen yeniden render edilmesini sağlayacak
    if (lang === 'tr' || lang === 'en') {
      currentLanguage.value = lang
    }
  })
  
  try {
    // Kullanıcı profilini al
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', user.value.id)
      .single()
    
    if (error) {
      // Eğer profil kaydı bulunamadıysa yeni bir tane oluştur
      if (error.code === 'PGRST116') { // No rows returned
        // Yeni bir profil kaydı oluştur
        await supabase
          .from('user_profiles')
          .insert([
            { user_id: user.value.id, email_notifications: true }
          ])
        
        // Varsayılan değer true
        emailNotifications.value = true
      } else {
        throw error
      }
    } else {
      // Varsayılan olarak true, eğer false olarak belirtilmişse false
      emailNotifications.value = profile.email_notifications !== false
    }
  } catch (err) {
    console.error('Profil bilgileri alınamadı:', err)
    toast.error(languageStore.t('home.settings.loading'))
  } finally {
    isLoading.value = false
  }
})

// Bileşen kaldırılırken temizlik işlemi
onUnmounted(() => {
  if (cleanupLanguageListener) {
    cleanupLanguageListener()
    cleanupLanguageListener = null
  }
})

const updateEmailNotifications = async () => {
  if (!user.value?.id) return
  
  isSaving.value = true
  
  try {
    const { error } = await supabase
      .from('user_profiles')
      .update({ email_notifications: emailNotifications.value })
      .eq('user_id', user.value.id)
    
    if (error) throw error
    
    toast.success(emailNotifications.value 
      ? languageStore.t('home.settings.emailNotificationsEnabled', { state: languageStore.t('common.enabled') })
      : languageStore.t('home.settings.emailNotificationsDisabled', { state: languageStore.t('common.disabled') })
    )
  } catch (err) {
    console.error('Ayarlar güncellenirken hata:', err)
    toast.error(languageStore.t('common.errors.unknownError'))
    // Hata durumunda eski değere geri dön
    emailNotifications.value = !emailNotifications.value
  } finally {
    isSaving.value = false
  }
}
</script> 